import { useAuth } from '../auth/AuthContext'
import { useBrand } from '../theme/ThemeProvider'
import { BrandLockupStacked } from './Brand'

/**
 * Stands between sign-in and the application when the request cannot be attributed to
 * exactly one organization.
 *
 * The API refuses rather than guessing in both of these cases, so the SPA has to
 * resolve them before any screen renders a number. Showing a chooser is the honest
 * response to "which organization's costs are these?" — picking one silently is not.
 */
export function OrganizationGate({ children }: { children: React.ReactNode }) {
  const { loading, error, hasNoOrganization, mustChooseOrganization, organizations, setActiveOrg } =
    useAuth()

  // A user with no organization has no theme either, so this is the product's own name.
  // It is still read from the brand rather than written here: a single-tenant white-label
  // deployment reaches this screen under its own name, and hard-coding "Tracer" into the
  // one screen that explains why nothing is visible would undo the whole exercise.
  const { productName } = useBrand()

  if (loading) {
    /*
        The lockup animates rather than a spinner appearing next to it.

        The mark already reads left to right as a process arriving at a number, so the
        wait is expressed by replaying it — see brand.css. The animation is decorative and
        aria-hidden, which is why the line below is a live region: a screen reader gets
        told what is happening, and someone who has asked for reduced motion gets the text
        with the movement removed rather than nothing at all.
    */
    return (
      <Centered waiting>
        <p
          role="status"
          aria-live="polite"
          className="text-[13px]"
          style={{ color: 'var(--color-muted)' }}
        >
          Loading your profile…
        </p>
      </Centered>
    )
  }

  if (error) {
    return (
      <Centered>
        <h1 className="mb-2 text-[17px]">Could not sign you in</h1>
        <p className="mb-4 max-w-md text-[13px]" style={{ color: 'var(--color-muted)' }}>
          {error}
        </p>
        <button className="btn" onClick={() => window.location.reload()}>
          Try again
        </button>
      </Centered>
    )
  }

  if (hasNoOrganization) {
    return (
      <Centered>
        <h1 className="mb-2 text-[17px]">No organization</h1>
        <p className="max-w-md text-[13px]" style={{ color: 'var(--color-muted)' }}>
          Your account is not a member of any organization, so there is nothing for{' '}
          {productName} to show you. Ask an administrator to add you to an organization
          group.
        </p>
      </Centered>
    )
  }

  if (mustChooseOrganization) {
    return (
      <Centered>
        <h1 className="mb-1 text-[17px]">Choose an organization</h1>
        <p className="mb-5 max-w-md text-[13px]" style={{ color: 'var(--color-muted)' }}>
          You belong to more than one. Everything you see and change from here belongs to
          the one you pick.
        </p>

        <ul
          className="w-80 overflow-hidden rounded border"
          style={{ borderColor: 'var(--color-rule)', background: 'var(--color-panel)' }}
        >
          {organizations.map((org, index) => (
            <li
              key={org.id}
              style={{
                borderTop: index === 0 ? undefined : '1px solid var(--color-rule-soft)',
              }}
            >
              <button
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left"
                onClick={() => setActiveOrg(org.id)}
              >
                <span className="flex-1 text-[13px] font-medium">{org.name}</span>
                <span className="ident" style={{ color: 'var(--color-faint)' }}>
                  {org.id}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Centered>
    )
  }

  return <>{children}</>
}

function Centered({ children, waiting = false }: { children: React.ReactNode; waiting?: boolean }) {
  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-6 px-6 text-center"
      aria-busy={waiting || undefined}
    >
      <BrandLockupStacked size={40} showTagline waiting={waiting} />
      <div className="flex flex-col items-center">{children}</div>
    </div>
  )
}
